import React, { useState, useEffect } from "react";
import {
  Table, Button, Input, Modal, Form, Select, Card, Row, Col, Space, Tag,
  Typography, Layout, Statistic, Tooltip, Radio, Progress, Badge
} from "antd";
import {
  FaPause, FaPlus, FaClock, FaCheck, FaCalendarAlt, FaUserTie, FaUtensils, FaInfoCircle
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/Api";

const { Title, Text } = Typography;
const { Option } = Select;
const { Content } = Layout;

// ─────────────────────────────────────────────
// Utilitários de tempo
// ─────────────────────────────────────────────
const timeToSeconds = (t) => {
  if (!t) return 0;
  const negative = t.startsWith("-");
  const clean = negative ? t.slice(1) : t;
  const [h = 0, m = 0, s = 0] = clean.split(":").map(Number);
  const total = h * 3600 + m * 60 + s;
  return negative ? -total : total;
};

const secondsToTime = (seconds) => {
  const abs = Math.abs(seconds);
  const h = String(Math.floor(abs / 3600)).padStart(2, "0");
  const m = String(Math.floor((abs % 3600) / 60)).padStart(2, "0");
  const s = String(abs % 60).padStart(2, "0");
  return `${seconds < 0 ? "-" : ""}${h}:${m}:${s}`;
};

// Percentual do saldo restante para a barra de progresso
const calcPercent = (saldo, total) => {
  const s = timeToSeconds(saldo);
  if (s <= 0) return 0;
  return Math.min(100, Math.round((s / total) * 100));
};

const TOTAL_ALMOCO_SEC = 3600;   // 1h
const TOTAL_SUPER_SEC  = 600;    // 10min

const formatDateTime = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// ─────────────────────────────────────────────
// Mapa de ícone/tipo por motivo
// ─────────────────────────────────────────────
const getMotivoInfo = (id) => {
  switch (id) {
    case 1:
    case 2:
      return { tipo: "almoco",     label: "Almoço",          icon: <FaUtensils />, color: "#1677ff" };
    case 3:
      return { tipo: "supervisor", label: "Supervisor",       icon: <FaUserTie />,  color: "#722ed1" };
    case 4:
      return { tipo: "livre",      label: "Sem desconto",     icon: <FaInfoCircle />, color: "#52c41a" };
    default:
      return { tipo: "outro",      label: "Outro",            icon: <FaPause />,    color: "#8c8c8c" };
  }
};

// ─────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────
const Pausa = () => {
  const [pausas,          setPausas]          = useState([]);
  const [motivos,         setMotivos]         = useState([]);
  const [mapaAlmoco,      setMapaAlmoco]      = useState([]);
  const [mapaSupervisor,  setMapaSupervisor]  = useState([]);

  const [saldo,    setSaldo]    = useState({ almoco: "01:00:00", supervisor: "00:10:00" });
  const [consumido, setConsumido] = useState({ almoco: "00:00:00", supervisor: "00:00:00" });

  // Loadings granulares
  const [loading,          setLoading]          = useState(false);
  const [loadingMapa,      setLoadingMapa]      = useState(false);
  const [loadingSubmit,    setLoadingSubmit]    = useState(false);
  const [loadingRetorno,   setLoadingRetorno]   = useState(null); // id da pausa em loading

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [form]                = Form.useForm();
  const [motivoSelecionado,   setMotivoSelecionado]  = useState(null);
  const [horarioSelecionado,  setHorarioSelecionado] = useState(null);

  const userStored = JSON.parse(localStorage.getItem("user"));

  // Derivados de saldo
  const saldoAlmocoSec    = timeToSeconds(saldo.almoco);
  const saldoSupervisorSec = timeToSeconds(saldo.supervisor);
  const semSaldoAlmoco    = saldoAlmocoSec    <= 0;
  const semSaldoSupervisor = saldoSupervisorSec <= 0;

  // Motivo id=4 nunca bloqueia
  const podeAlmoco      = !semSaldoAlmoco;
  const podeSupervisor  = !semSaldoSupervisor;

  // Pode abrir modal se houver qualquer opção disponível
  const podeSolicitar = podeAlmoco || podeSupervisor || motivos.some((m) => m.id === 4);

  // ─── Efeitos ──────────────────────────────
  useEffect(() => {
    document.title = "PAUSAS";
    fetchMotivos();
    fetchPausas();
  }, []);

  // ─── API ──────────────────────────────────
  const fetchMotivos = async () => {
    try {
      const response = await api.ListMotivo();
      setMotivos(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar motivos");
    }
  };

  const fetchPausas = async () => {
    try {
      setLoading(true);
      const resp = await api.ListPausa({ id_user: userStored.id });
      if (resp?.pausas) {
        setPausas(resp.pausas);
        setConsumido(resp.consumido);
        setSaldo(resp.saldo);
      }
    } catch {
      toast.error("Erro ao carregar pausas");
    } finally {
      setLoading(false);
    }
  };

  const fetchMapaHorarios = async (idMotivo) => {
    try {
      setLoadingMapa(true);
      setHorarioSelecionado(null);

      if (idMotivo === 1 || idMotivo === 2) {
        const response = await api.ListMapaAlmoco();
        setMapaAlmoco(Array.isArray(response) ? response : []);
        setMapaSupervisor([]);
      } else if (idMotivo === 3) {
        const response = await api.ListMapaSupervisor();
        setMapaSupervisor(Array.isArray(response) ? response : []);
        setMapaAlmoco([]);
      } else {
        setMapaAlmoco([]);
        setMapaSupervisor([]);
      }
    } catch {
      toast.error("Erro ao carregar horários disponíveis");
    } finally {
      setLoadingMapa(false);
    }
  };

  // ─── Handlers ─────────────────────────────
  const handleMotivoChange = (value) => {
    setMotivoSelecionado(value);
    setHorarioSelecionado(null);
    form.setFieldsValue({ agendamento: null });

    if (value === 1 || value === 2 || value === 3) {
      fetchMapaHorarios(value);
    } else {
      setMapaAlmoco([]);
      setMapaSupervisor([]);
    }
  };

  const handleAddPausa = async (values) => {
    try {
      setLoadingSubmit(true);
      const motivoId = values.id_motivo;

      // Validação de saldo por tipo
      if ((motivoId === 1 || motivoId === 2) && semSaldoAlmoco) {
        toast.error("Sem saldo disponível para pausa de almoço.");
        return;
      }
      if (motivoId === 3 && semSaldoSupervisor) {
        toast.error("Sem saldo disponível para falar com supervisor.");
        return;
      }

      // Agendamento obrigatório para motivos 1, 2 e 3
      if ((motivoId === 1 || motivoId === 2 || motivoId === 3) && !values.agendamento) {
        toast.error("Selecione um horário para agendar a pausa.");
        return;
      }

      const payload = {
        id_user:    userStored.id,
        id_motivo:  motivoId,
        obs:        values.obs || "",
        agendamento: values.agendamento || null
      };

      const resp = await api.AddPausa(payload);

      if (resp?.success) {
        toast.success("Pausa solicitada com sucesso!");
        handleCloseModal();
        fetchPausas();
      } else {
        toast.error(resp?.message || "Erro ao solicitar pausa.");
      }
    } catch {
      toast.error("Erro ao adicionar pausa");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleConfirmarRetorno = async (pausa) => {
    try {
      setLoadingRetorno(pausa.id);
      const payload = {
        id:      pausa.id,
        status:  3,
        retorno: formatDateTime(new Date()),
      };
      const resp = await api.AlterPausa(payload);
      if (resp?.success) {
        toast.success("Retorno confirmado!");
        fetchPausas();
      }
    } catch {
      toast.error("Erro ao confirmar retorno");
    } finally {
      setLoadingRetorno(null);
    }
  };

  const handleCloseModal = () => {
    setIsAddModalVisible(false);
    form.resetFields();
    setMotivoSelecionado(null);
    setHorarioSelecionado(null);
    setMapaAlmoco([]);
    setMapaSupervisor([]);
  };

  // ─── Renderização do mapa de horários ─────
  const renderMapaHorarios = () => {
    const mapa = motivoSelecionado === 3 ? mapaSupervisor : mapaAlmoco;

    if (loadingMapa) {
      return (
        <div style={{ textAlign: "center", padding: "16px", color: "#1677ff" }}>
          <FaClock style={{ marginRight: 8 }} />
          Carregando horários disponíveis...
        </div>
      );
    }

    if (mapa.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "16px", color: "#999" }}>
          Nenhum horário disponível para agendamento
        </div>
      );
    }

    return (
      <div style={{ maxHeight: "200px", overflowY: "auto", marginTop: "8px" }}>
        <Radio.Group
          onChange={(e) => {
            setHorarioSelecionado(e.target.value);
            form.setFieldsValue({ agendamento: e.target.value });
          }}
          value={horarioSelecionado}
          style={{ width: "100%" }}
        >
          {mapa.map((item, index) => (
            <div
              key={index}
              style={{
                marginBottom: "8px",
                padding: "8px 12px",
                border: `1px solid ${horarioSelecionado === item.horario_disponivel ? "#1677ff" : "#e8e8e8"}`,
                borderRadius: "8px",
                background: horarioSelecionado === item.horario_disponivel ? "#e6f4ff" : "#fafafa",
                transition: "all 0.2s"
              }}
            >
              <Radio value={item.horario_disponivel}>
                <Space>
                  <FaClock style={{ color: "#1677ff" }} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Text strong>Início: {item.horario_disponivel}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>Término: {item.fim}</Text>
                  </div>
                </Space>
              </Radio>
            </div>
          ))}
        </Radio.Group>
      </div>
    );
  };

  // ─── Banner informativo do motivo ─────────
  const renderInfoMotivo = () => {
    if (!motivoSelecionado) return null;

    const info = {
      1: { msg: "Pausa de almoço — desconta do saldo de 1h.",          color: "#1677ff", bg: "#e6f4ff" },
      2: { msg: "Pausa de almoço — desconta do saldo de 1h.",          color: "#1677ff", bg: "#e6f4ff" },
      3: { msg: "Pausa com supervisor — desconta do saldo de 10 min.", color: "#722ed1", bg: "#f9f0ff" },
      4: { msg: "Esta pausa não desconta nenhum saldo.",               color: "#52c41a", bg: "#f6ffed" },
    }[motivoSelecionado];

    if (!info) return null;

    return (
      <div style={{
        padding: "10px 14px",
        borderRadius: "8px",
        backgroundColor: info.bg,
        border: `1px solid ${info.color}`,
        marginBottom: 16,
        color: info.color,
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        gap: 8
      }}>
        <FaInfoCircle />
        {info.msg}
      </div>
    );
  };

  // ─── Colunas da tabela ────────────────────
  const columns = [
    {
      title: "Motivo",
      dataIndex: "id_motivo",
      render: (id) => {
        const motivo = motivos.find((m) => m.id === id);
        const info   = getMotivoInfo(id);
        return (
          <Space>
            <span style={{ color: info.color }}>{info.icon}</span>
            <span>{motivo ? motivo.descricao : "Desconhecido"}</span>
          </Space>
        );
      }
    },
    {
      title: "Agendamento",
      dataIndex: "agendamento",
      render: (t) => t ? new Date(t).toLocaleString() : <Text type="secondary">—</Text>
    },
    {
      title: "Observação",
      dataIndex: "obs",
      render: (t) =>
        t ? (
          <Tooltip title={t}>
            <Text style={{ maxWidth: 150, display: "inline-block" }} ellipsis>{t}</Text>
          </Tooltip>
        ) : <Text type="secondary">—</Text>
    },
    {
      title: "Saída",
      dataIndex: "aprovacao",
      render: (t) => t ? new Date(t).toLocaleString() : <Text type="secondary">—</Text>
    },
    {
      title: "Retorno",
      dataIndex: "retorno",
      render: (t) => t ? new Date(t).toLocaleString() : <Text type="secondary">—</Text>
    },
    {
      title: "Desconto",
      dataIndex: "id_motivo",
      render: (id) => {
        const info = getMotivoInfo(id);
        if (info.tipo === "almoco")      return <Tag color="blue">1h</Tag>;
        if (info.tipo === "supervisor")  return <Tag color="purple">10min</Tag>;
        if (info.tipo === "livre")       return <Tag color="green">Sem desconto</Tag>;
        return <Tag>—</Tag>;
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => {
        const map = {
          0: { color: "orange", txt: "Pendente" },
          1: { color: "green",  txt: "Aprovado" },
          2: { color: "red",    txt: "Rejeitado" },
          3: { color: "blue",   txt: "Retornado" }
        };
        const item = map[s] || { color: "default", txt: "—" };
        return <Tag color={item.color}>{item.txt}</Tag>;
      }
    },
    {
      title: "Ação",
      render: (_, record) =>
        String(record.status) === "1" ? (
          <Button
            type="primary"
            icon={<FaCheck />}
            loading={loadingRetorno === record.id}
            onClick={() => handleConfirmarRetorno(record)}
            size="small"
          >
            Confirmar Retorno
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        )
    }
  ];

  // ─── Render ───────────────────────────────
  return (
    <Layout>
      <Content style={{ padding: 24 }}>
        <ToastContainer />

        <Card
          title={
            <Space>
              <FaPause style={{ fontSize: 20, color: "#1677ff" }} />
              <Title level={4} style={{ margin: 0 }}>Gestão de Pausas</Title>
            </Space>
          }
          extra={
            <Tooltip title={!podeSolicitar ? "Sem saldo disponível" : ""}>
              <Button
                type="primary"
                icon={<FaPlus />}
                onClick={() => setIsAddModalVisible(true)}
                disabled={!podeSolicitar}
              >
                Solicitar Pausa
              </Button>
            </Tooltip>
          }
        >
          {/* ── Cards de Saldo ── */}
          <Row gutter={16} style={{ marginBottom: 24 }}>

            {/* Saldo Almoço */}
            <Col xs={24} sm={12}>
              <Card
                size="small"
                hoverable
                style={{
                  borderLeft: `4px solid ${semSaldoAlmoco ? "#ff4d4f" : "#1677ff"}`,
                  borderRadius: 10
                }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <FaUtensils style={{ color: semSaldoAlmoco ? "#ff4d4f" : "#1677ff" }} />
                    <Text strong>Saldo de Almoço</Text>
                    {semSaldoAlmoco && <Badge status="error" text="Esgotado" />}
                  </Space>
                  <Statistic
                    value={saldo.almoco}
                    valueStyle={{
                      color: semSaldoAlmoco ? "#ff4d4f" : "#1677ff",
                      fontSize: 22
                    }}
                    prefix={<FaClock style={{ marginRight: 4 }} />}
                  />
                  <Progress
                    percent={calcPercent(saldo.almoco, TOTAL_ALMOCO_SEC)}
                    showInfo={false}
                    strokeColor={semSaldoAlmoco ? "#ff4d4f" : "#1677ff"}
                    trailColor="#e8e8e8"
                    size="small"
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Consumido: {consumido.almoco} / 01:00:00
                  </Text>
                </Space>
              </Card>
            </Col>

            {/* Saldo Supervisor */}
            <Col xs={24} sm={12}>
              <Card
                size="small"
                hoverable
                style={{
                  borderLeft: `4px solid ${semSaldoSupervisor ? "#ff4d4f" : "#722ed1"}`,
                  borderRadius: 10
                }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <FaUserTie style={{ color: semSaldoSupervisor ? "#ff4d4f" : "#722ed1" }} />
                    <Text strong>Saldo Supervisor</Text>
                    {semSaldoSupervisor && <Badge status="error" text="Esgotado" />}
                  </Space>
                  <Statistic
                    value={saldo.supervisor}
                    valueStyle={{
                      color: semSaldoSupervisor ? "#ff4d4f" : "#722ed1",
                      fontSize: 22
                    }}
                    prefix={<FaClock style={{ marginRight: 4 }} />}
                  />
                  <Progress
                    percent={calcPercent(saldo.supervisor, TOTAL_SUPER_SEC)}
                    showInfo={false}
                    strokeColor={semSaldoSupervisor ? "#ff4d4f" : "#722ed1"}
                    trailColor="#e8e8e8"
                    size="small"
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Consumido: {consumido.supervisor} / 00:10:00
                  </Text>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* ── Tabela ── */}
          <Table
            columns={columns}
            dataSource={pausas}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 8 }}
            scroll={{ x: 800 }}
          />
        </Card>

        {/* ── Modal Solicitar Pausa ── */}
        <Modal
          title={
            <Space>
              <FaCalendarAlt style={{ color: "#1677ff" }} />
              <span>Solicitar Pausa</span>
            </Space>
          }
          open={isAddModalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={580}
          destroyOnClose
        >
          <Form layout="vertical" form={form} onFinish={handleAddPausa}>

            {/* Motivo */}
            <Form.Item
              name="id_motivo"
              label="Motivo da Pausa"
              rules={[{ required: true, message: "Selecione um motivo" }]}
            >
              <Select placeholder="Escolha o motivo" onChange={handleMotivoChange}>
                {motivos.map((m) => {
                  const isAlmoco     = m.id === 1 || m.id === 2;
                  const isSupervisor = m.id === 3;
                  const disabled =
                    (isAlmoco && semSaldoAlmoco) ||
                    (isSupervisor && semSaldoSupervisor);

                  const info = getMotivoInfo(m.id);

                  return (
                    <Option key={m.id} value={m.id} disabled={disabled}>
                      <Space>
                        <span style={{ color: info.color }}>{info.icon}</span>
                        {m.descricao}
                        {isAlmoco     && <Tag color="blue"   style={{ marginLeft: 4 }}>-1h</Tag>}
                        {isSupervisor && <Tag color="purple" style={{ marginLeft: 4 }}>-10min</Tag>}
                        {m.id === 4   && <Tag color="green"  style={{ marginLeft: 4 }}>Sem desconto</Tag>}
                        {disabled     && <Tag color="red"    style={{ marginLeft: 4 }}>Sem saldo</Tag>}
                      </Space>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>

            {/* Banner informativo */}
            {renderInfoMotivo()}

            {/* Mapa de horários (motivos 1, 2, 3) */}
            {(motivoSelecionado === 1 || motivoSelecionado === 2 || motivoSelecionado === 3) && (
              <Form.Item
                name="agendamento"
                label={
                  <Space>
                    <FaClock />
                    <span>Selecione o horário</span>
                  </Space>
                }
                rules={[{ required: true, message: "Selecione um horário" }]}
              >
                {renderMapaHorarios()}
              </Form.Item>
            )}

            {/* Observação */}
            <Form.Item name="obs" label="Observação (opcional)">
              <Input.TextArea rows={3} maxLength={200} showCount />
            </Form.Item>

            {/* Botões */}
            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={handleCloseModal} disabled={loadingSubmit}>
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loadingSubmit}
                  icon={<FaCalendarAlt />}
                >
                  {loadingSubmit ? "Solicitando..." : "Solicitar Pausa"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default Pausa;